/**
 * 📣 **What the notice line says without being asked** — and how long any
 * notice stays up. Pure, so a test reaches it; `main.ts` calls both.
 *
 * 🍂 **T257 — réttir opening and closing.** 🎯 haugur, 2026-10-05, choosing how
 * the autumn window is taught: *"1 and 2"* — a note after a kill (the server,
 * which knows what you got) and **a note when the window opens and closes**
 * (here, off the calendar the client already shows).
 *
 * ⚠️ **Why the client and not a server broadcast:** the client carries the game
 * clock forward by itself, so it already knows the moment the date turns — and
 * it alone knows when the player is actually LOOKING. A note sent while the
 * game menu covers the screen is a note nobody read.
 */

import { isRettir } from '@lofi/shared';

/** Which réttir note to show, if any. */
export type RettirNote = 'open' | 'close' | null;

/**
 * 🍂 **One step of the réttir watch.**
 *
 * @param told what the player was last told — in the window, out of it, or
 *   `null` before the first look this session.
 * @returns the new state, and the note to show now.
 *
 * 🔴 **The first look of a session, DURING réttir, says it is on** — or a player
 * who logs in on 3 October never hears of it. ⚠️ **The first look outside it
 * says nothing**: *"réttir is over"* to somebody who never knew it began is
 * noise.
 */
export function rettirStep(
  told: boolean | null,
  month: number,
  dayOfMonth: number,
): { told: boolean; note: RettirNote } {
  const now = isRettir(month, dayOfMonth);
  if (told === null) return { told: now, note: now ? 'open' : null };
  if (now === told) return { told, note: null };
  return { told: now, note: now ? 'open' : 'close' };
}

/**
 * ⏱️ **How long a notice stays on screen, in milliseconds.**
 *
 * **3.5 s for a short one, longer for a long one** — about 60 ms a character,
 * a comfortable reading pace. 🔴 The réttir notes are a hundred characters,
 * and at a flat 3.5 s they were gone before the end. ⚠️ A short refusal stays
 * short, so a stale *"too far"* never sits on screen looking like it is about
 * what you did next.
 */
export function noticeMs(text: string): number {
  return Math.max(3500, text.length * 60);
}
